// Tiny procedural low-poly mesh builder. Accumulates flat-shaded triangles with
// per-vertex colour into one non-indexed BufferGeometry (same style as the terrain
// mesh), so a whole model is one draw call with a single shared material. Pure
// geometry — no scene, no state. Owner: client-render3d/models.

import { BufferAttribute, BufferGeometry, Color } from 'three'

// Per-face shading so faceted forms read even under flat lighting: top brightest,
// sides mid, bottom darkest. Multiplied into the face colour.
const FACE = { top: 1.0, side: 0.9, bottom: 0.66 }

type Col = Color | string | number

export class MeshBuilder {
  private pos: number[] = []
  private col: number[] = []
  private _c = new Color()

  /** Merge another builder's triangles, rotated about X (radians, around the other
   *  builder's origin) and then translated — used to pose a gripped tool in a
   *  merged static figure. */
  merge(other: MeshBuilder, tx = 0, ty = 0, tz = 0, rx = 0): void {
    const c = Math.cos(rx), si = Math.sin(rx)
    for (let i = 0; i < other.pos.length; i += 3) {
      const x = other.pos[i], y = other.pos[i + 1], z = other.pos[i + 2]
      this.pos.push(x + tx, y * c - z * si + ty, y * si + z * c + tz)
    }
    for (const v of other.col) this.col.push(v)
  }

  private rgb(c: Col): [number, number, number] {
    const k = c instanceof Color ? c : this._c.set(c as string)
    return [k.r, k.g, k.b]
  }

  /** One triangle, CCW seen from outside. */
  tri(
    ax: number, ay: number, az: number,
    bx: number, by: number, bz: number,
    cx: number, cy: number, cz: number,
    c: Col, shade = 1,
  ): void {
    const [r, g, b] = this.rgb(c)
    this.pos.push(ax, ay, az, bx, by, bz, cx, cy, cz)
    for (let i = 0; i < 3; i++) this.col.push(r * shade, g * shade, b * shade)
  }

  /** Quad a→b→c→d (CCW seen from outside) as two triangles. */
  quad(
    a: number[], b: number[], c: number[], d: number[], col: Col, shade = 1,
  ): void {
    this.tri(a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2], col, shade)
    this.tri(a[0], a[1], a[2], c[0], c[1], c[2], d[0], d[1], d[2], col, shade)
  }

  /** Axis-aligned box centred at (cx,cy,cz), size w×h×d, optional yaw about its
   *  own centre. Feet-based callers pass cy = y0 + h/2. */
  box(cx: number, cy: number, cz: number, w: number, h: number, d: number, col: Col, rotY = 0): void {
    const hw = w / 2, hh = h / 2, hd = d / 2
    const co = Math.cos(rotY), si = Math.sin(rotY)
    // 8 corners
    const P = (sx: number, sy: number, sz: number): number[] => {
      const x = sx * hw, z = sz * hd
      return [cx + x * co - z * si, cy + sy * hh, cz + x * si + z * co]
    }
    const A = P(-1, 1, -1), B = P(1, 1, -1), C = P(1, 1, 1), D = P(-1, 1, 1) // top
    const E = P(-1, -1, -1), F = P(1, -1, -1), G = P(1, -1, 1), H = P(-1, -1, 1) // bottom
    this.quad(A, D, C, B, col, FACE.top)
    this.quad(E, F, G, H, col, FACE.bottom)
    this.quad(D, A, E, H, col, FACE.side) // -x
    this.quad(B, C, G, F, col, FACE.side) // +x
    this.quad(A, B, F, E, col, FACE.side) // -z
    this.quad(C, D, H, G, col, FACE.side) // +z
  }

  /** Gable (triangular-prism) roof: rectangular eaves w×d at y=y0, ridge line of
   *  length d running along z, raised h. Gable triangles face ±x. */
  gable(cx: number, y0: number, cz: number, w: number, h: number, d: number, col: Col, rotY = 0): void {
    const hw = w / 2, hd = d / 2
    const co = Math.cos(rotY), si = Math.sin(rotY)
    const P = (x: number, y: number, z: number): number[] =>
      [cx + x * co - z * si, y0 + y, cz + x * si + z * co]
    const eL0 = P(-hw, 0, -hd), eL1 = P(-hw, 0, hd)
    const eR0 = P(hw, 0, -hd), eR1 = P(hw, 0, hd)
    const r0 = P(0, h, -hd), r1 = P(0, h, hd)
    this.quad(eL1, eL0, r0, r1, col, FACE.side)   // -x slope
    this.quad(eR0, eR1, r1, r0, col, FACE.side)   // +x slope
    this.tri(eL0[0], eL0[1], eL0[2], eR0[0], eR0[1], eR0[2], r0[0], r0[1], r0[2], col, FACE.top) // -z gable
    this.tri(eR1[0], eR1[1], eR1[2], eL1[0], eL1[1], eL1[2], r1[0], r1[1], r1[2], col, FACE.top) // +z gable
  }

  /** n-gon pyramid roof/spire: base radius rad at y0, apex at y0+h. */
  pyramid(cx: number, y0: number, cz: number, rad: number, h: number, sides: number, col: Col, phase = 0): void {
    const apex = [cx, y0 + h, cz]
    for (let i = 0; i < sides; i++) {
      const a0 = phase + (i / sides) * Math.PI * 2
      const a1 = phase + ((i + 1) / sides) * Math.PI * 2
      const p0 = [cx + Math.cos(a0) * rad, y0, cz + Math.sin(a0) * rad]
      const p1 = [cx + Math.cos(a1) * rad, y0, cz + Math.sin(a1) * rad]
      const sh = FACE.side + (i % 2) * 0.06
      this.tri(p0[0], p0[1], p0[2], p1[0], p1[1], p1[2], apex[0], apex[1], apex[2], col, sh)
    }
  }

  /** Vertical n-sided prism ("cylinder"): centre base (cx,y0,cz), radius rad, height h. */
  cyl(cx: number, y0: number, cz: number, rad: number, h: number, sides: number, col: Col, cap = true): void {
    const top = y0 + h
    for (let i = 0; i < sides; i++) {
      const a0 = (i / sides) * Math.PI * 2, a1 = ((i + 1) / sides) * Math.PI * 2
      const x0 = cx + Math.cos(a0) * rad, z0 = cz + Math.sin(a0) * rad
      const x1 = cx + Math.cos(a1) * rad, z1 = cz + Math.sin(a1) * rad
      const sh = FACE.side + (i % 2) * 0.05
      this.quad([x0, top, z0], [x0, y0, z0], [x1, y0, z1], [x1, top, z1], col, sh)
      if (cap) this.tri(cx, top, cz, x0, top, z0, x1, top, z1, col, FACE.top)
    }
  }

  /** Faceted ellipsoid, with a broad middle ring and tapered shoulders. */
  gem(cx: number, cy: number, cz: number, w: number, h: number, d: number, col: Col, sides = 6): void {
    const ring = (i: number, y: number, scale: number): number[] => {
      const a = i / sides * Math.PI * 2
      return [cx + Math.cos(a) * w / 2 * scale, cy + y * h, cz + Math.sin(a) * d / 2 * scale]
    }
    for (let i = 0; i < sides; i++) {
      const a = ring(i, -0.26, 0.82), b = ring(i + 1, -0.26, 0.82)
      const c = ring(i + 1, 0.22, 1), e = ring(i, 0.22, 1)
      this.quad(a, e, c, b, col, 0.88 + (i % 3) * 0.045)
      this.tri(cx, cy + h / 2, cz, c[0], c[1], c[2], e[0], e[1], e[2], col)
      this.tri(cx, cy - h / 2, cz, a[0], a[1], a[2], b[0], b[1], b[2], col, 0.7)
    }
  }

  /** A square timber between arbitrary endpoints; useful for braces and antlers. */
  beam(a: number[], b: number[], width: number, col: Col): void {
    const dx = b[0] - a[0], dy = b[1] - a[1], dz = b[2] - a[2]
    const len = Math.hypot(dx, dy, dz)
    if (len < 0.0001) return
    const n = [dx / len, dy / len, dz / len]
    const ref = Math.abs(n[1]) > 0.9 ? [1, 0, 0] : [0, 1, 0]
    const u = [n[1] * ref[2] - n[2] * ref[1], n[2] * ref[0] - n[0] * ref[2], n[0] * ref[1] - n[1] * ref[0]]
    const ul = Math.hypot(...u)
    for (let j = 0; j < 3; j++) u[j] /= ul
    const v = [n[1] * u[2] - n[2] * u[1], n[2] * u[0] - n[0] * u[2], n[0] * u[1] - n[1] * u[0]]
    const p = (at: number[], s: number, t: number): number[] => at.map((x, j) => x + (s * u[j] + t * v[j]) * width / 2)
    const ends = [a, b].map(at => [p(at, -1, -1), p(at, 1, -1), p(at, 1, 1), p(at, -1, 1)])
    for (let i = 0; i < 4; i++) this.quad(ends[0][i], ends[0][(i + 1) % 4], ends[1][(i + 1) % 4], ends[1][i], col, 0.9)
    this.quad(ends[0][3], ends[0][2], ends[0][1], ends[0][0], col, 0.7)
    this.quad(ends[1][0], ends[1][1], ends[1][2], ends[1][3], col)
  }

  /** Merge another builder's triangles (already in world/local space). */
  add(o: MeshBuilder): void {
    for (const v of o.pos) this.pos.push(v)
    for (const v of o.col) this.col.push(v)
  }

  get empty(): boolean { return this.pos.length === 0 }

  geometry(): BufferGeometry {
    const g = new BufferGeometry()
    g.setAttribute('position', new BufferAttribute(new Float32Array(this.pos), 3))
    g.setAttribute('color', new BufferAttribute(new Float32Array(this.col), 3))
    g.computeVertexNormals()
    return g
  }
}
